import { useCallback, useEffect, useState } from "react";
import { crDate, crDateTime, crLocalToISO, isoToCRInput } from "@/lib/dateFormat";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/i18n";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pin, MessageCircle, Trash2, Megaphone, Send, Pencil, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const REACTIONS = [
  { key: "like", icon: "👍" },
  { key: "love", icon: "❤️" },
  { key: "party", icon: "🎉" },
  { key: "clap", icon: "👏" },
  { key: "wow", icon: "😮" },
];

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/** Community board: news & comunicados with emoji reactions and comments. */
export const WallFeed = ({ limit = 10, allLink = null }) => {
  const { user } = useAuth();
  const { t } = useI18n();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openComments, setOpenComments] = useState({});
  const [comments, setComments] = useState({});
  const [draft, setDraft] = useState({});
  const [editing, setEditing] = useState(null);
  const isAcademic = ["superadmin", "academic_director"].includes(user?.role);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/wall/posts", { params: { limit } });
      setPosts(r.data || []);
    } finally { setLoading(false); }
  }, [limit]);

  useEffect(() => { load(); }, [load]);

  const react = async (post, emoji) => {
    const before = post.my_reaction;
    setPosts((prev) => prev.map((p) => {
      if (p.id !== post.id) return p;
      const rx = { ...(p.reactions || {}) };
      if (before) rx[before] = { ...rx[before], count: Math.max(0, (rx[before]?.count || 1) - 1) };
      const next = before === emoji ? null : emoji;
      if (next) rx[next] = { ...rx[next], count: (rx[next]?.count || 0) + 1 };
      return { ...p, my_reaction: next, reactions: rx };
    }));
    try { await api.post(`/wall/posts/${post.id}/react`, { emoji }); } catch { load(); }
  };

  const toggleComments = async (post) => {
    const open = !openComments[post.id];
    setOpenComments((o) => ({ ...o, [post.id]: open }));
    if (open && !comments[post.id]) {
      const r = await api.get(`/wall/posts/${post.id}/comments`);
      setComments((c) => ({ ...c, [post.id]: r.data || [] }));
    }
  };

  const sendComment = async (post) => {
    const text = (draft[post.id] || "").trim();
    if (!text) return;
    try {
      const r = await api.post(`/wall/posts/${post.id}/comments`, { text });
      setComments((c) => ({ ...c, [post.id]: [...(c[post.id] || []), r.data] }));
      setDraft((d) => ({ ...d, [post.id]: "" }));
      setPosts((prev) => prev.map((p) => (p.id === post.id ? { ...p, comment_count: (p.comment_count || 0) + 1 } : p)));
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo comentar"); }
  };

  const removeComment = async (post, c) => {
    await api.delete(`/wall/comments/${c.id}`);
    setComments((s) => ({ ...s, [post.id]: (s[post.id] || []).filter((x) => x.id !== c.id) }));
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/wall/posts/${editing.id}`, {
        title: editing.title, body: editing.body, audience: editing.audience,
        pinned: editing.pinned, scheduled_for: editing.scheduled_for || null,
      });
      setEditing(null);
      load();
      toast.success("Comunicado actualizado");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
  };

  const removePost = async (p) => {
    if (!window.confirm(`¿Borrar "${p.title}"? Se eliminan también sus reacciones y comentarios.`)) return;
    await api.delete(`/wall/posts/${p.id}`);
    load();
  };

  if (loading) return null;

  return (
    <section data-testid="wall-feed">
      <div className="flex items-center gap-2 mb-3">
        <Megaphone className="h-4 w-4 text-brand-blue" />
        <h2 className="text-base font-bold text-slate-900 flex-1">{t("wall_news_title")}</h2>
        {allLink && (
          <Link to={allLink} className="text-xs font-semibold text-brand-blue hover:underline" data-testid="wall-see-all">
            {t("wall_see_all")} →
          </Link>
        )}
      </div>

      {posts.length === 0 ? (
        <Card className="p-8 text-center text-sm text-slate-500" data-testid="wall-empty">
          {t("wall_empty_text")}
        </Card>
      ) : (
        <div className="space-y-4">
          {posts.map((p) => (
            <Card key={p.id} className="overflow-hidden border-slate-200" data-testid={`wall-post-${p.id}`}>
              {p.has_image && (
                <img
                  src={`${API_BASE}/api/wall/posts/${p.id}/image`}
                  alt={p.title}
                  className="w-full max-h-72 object-cover"
                />
              )}
              <div className="p-5">
                <div className="flex items-start gap-2">
                  {p.pinned && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shrink-0" data-testid={`wall-pinned-${p.id}`}>
                      <Pin className="h-3 w-3" /> Fijado
                    </span>
                  )}
                  <h3 className="font-bold text-slate-900 leading-tight flex-1">{p.title}</h3>
                  {isAcademic && (
                    <div className="flex gap-1 shrink-0">
                      <Button size="icon" variant="ghost" className="h-8 w-8"
                        onClick={() => setEditing({ ...p, scheduled_for: p.scheduled_for || "" })}
                        data-testid={`wall-edit-${p.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600"
                        onClick={() => removePost(p)} data-testid={`wall-delete-${p.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
                {p.scheduled_for && new Date(p.scheduled_for) > new Date() && (
                  <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 text-[10px] font-semibold" data-testid={`wall-scheduled-${p.id}`}>
                    <Clock className="h-3 w-3" /> Se publica el {crDateTime(p.scheduled_for)}
                  </p>
                )}
                <p className="text-[11px] text-slate-500 mt-1">
                  {p.author_name} · {crDate(p.created_at)}
                  {p.audience !== "all" && ` · ${p.audience === "students" ? "estudiantes" : "profesores"}`}
                </p>
                <p className="text-sm text-slate-700 mt-3 whitespace-pre-line">{p.body}</p>

                <div className="mt-4 flex flex-wrap items-center gap-1.5">
                  {REACTIONS.map((r) => {
                    const count = p.reactions?.[r.key]?.count || 0;
                    const mine = p.my_reaction === r.key;
                    return (
                      <button
                        key={r.key}
                        type="button"
                        onClick={() => react(p, r.key)}
                        title={(p.reactions?.[r.key]?.names || []).join(", ")}
                        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors ${mine ? "border-brand-blue bg-blue-50 text-brand-blue font-semibold" : "border-slate-200 text-slate-500 hover:border-slate-300 hover:bg-slate-50"}`}
                        data-testid={`wall-react-${p.id}-${r.key}`}
                      >
                        <span className="text-sm leading-none">{r.icon}</span>
                        {count > 0 && <span data-testid={`wall-react-count-${p.id}-${r.key}`}>{count}</span>}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => toggleComments(p)}
                    className="ml-auto inline-flex items-center gap-1 text-xs text-slate-500 hover:text-brand-blue transition-colors"
                    data-testid={`wall-comments-toggle-${p.id}`}
                  >
                    <MessageCircle className="h-3.5 w-3.5" /> {t("wall_comments_n", { n: p.comment_count || 0 })}
                  </button>
                </div>

                {openComments[p.id] && (
                  <div className="mt-4 border-t border-slate-100 pt-3 space-y-3" data-testid={`wall-comments-${p.id}`}>
                    {(comments[p.id] || []).map((c) => (
                      <div key={c.id} className="flex items-start gap-2" data-testid={`wall-comment-${c.id}`}>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-slate-800">{c.user_name}</p>
                          <p className="text-sm text-slate-700">{c.text}</p>
                        </div>
                        {(isAcademic || c.user_id === user?.user_id) && (
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => removeComment(p, c)} data-testid={`wall-comment-delete-${c.id}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <Input
                        placeholder={t("write_comment_ph")}
                        value={draft[p.id] || ""}
                        onChange={(e) => setDraft((d) => ({ ...d, [p.id]: e.target.value }))}
                        onKeyDown={(e) => e.key === "Enter" && sendComment(p)}
                        data-testid={`wall-comment-input-${p.id}`}
                      />
                      <Button size="icon" onClick={() => sendComment(p)} data-testid={`wall-comment-send-${p.id}`}>
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="wall-edit-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Editar comunicado</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={editing?.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="wall-edit-title" />
            <Textarea rows={5} value={editing?.body || ""} onChange={(e) => setEditing({ ...editing, body: e.target.value })} data-testid="wall-edit-body" />
            <div className="flex flex-wrap items-center gap-4">
              <div className="w-48">
                <Select value={editing?.audience || "all"} onValueChange={(v) => setEditing({ ...editing, audience: v })}>
                  <SelectTrigger data-testid="wall-edit-audience"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toda la comunidad</SelectItem>
                    <SelectItem value="students">Solo estudiantes</SelectItem>
                    <SelectItem value="teachers">Solo profesores</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!editing?.pinned} onCheckedChange={(v) => setEditing({ ...editing, pinned: v })} data-testid="wall-edit-pinned" />
                <span className="text-sm text-slate-600">Fijar arriba</span>
              </div>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Programar publicación (opcional)</p>
              <Input type="datetime-local"
                value={isoToCRInput(editing?.scheduled_for)}
                onChange={(e) => setEditing({ ...editing, scheduled_for: crLocalToISO(e.target.value) })}
                data-testid="wall-edit-schedule" />
              <p className="text-[11px] text-slate-500 mt-1">Hora de Costa Rica</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={saveEdit} data-testid="wall-edit-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default WallFeed;
